import React, { useEffect, useState } from 'react';

import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import Button from '@shared/atoms/Button';
import Input from '@shared/atoms/Input';
import Select from '@shared/atoms/Select';
import T from '@shared/atoms/Typography';

import { GAME_TYPE } from '@shared/const';
import theme from '@shared/themes/default';

import { useCourse } from '../../../../../contexts/CourseContext';

const ContentWrapper = styled.div`
  padding: ${theme.spacing.m1} 0 0;
`;

const InputButtonWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  gap: ${theme.spacing.s};
  width: 100%;
`;

const SelectWrapper = styled.div`
  width: 40%;
  margin-top: ${theme.spacing.xl};
`;

const GAME_LENGTH_OPTIONS = [300, 600, 900, 1200];

const GameInput = ({ currentGame, onSubmit }) => {
  const [gameName, setGameName] = useState('');
  const [gameType, setGameType] = useState(currentGame?.type || GAME_TYPE.GAME);
  const [gameLength, setGameLength] = useState(currentGame?.duration || 0);
  const [gameId, setGameId] = useState(currentGame?.externalGameId || '');
  const [isValidated, setIsValidated] = useState(false);
  const [hasValidated, setHasValidated] = useState(false);

  const { validateGameQuery, validateGameResponse, locked, setHasChanged } =
    useCourse();
  const { t } = useTranslation();

  const validateGameId = async () => {
    if (gameId?.length > 0) {
      setHasValidated(true);
      validateGameQuery({
        variables: { gameId },
      });
    }
  };

  useEffect(() => {
    const { data, error, loading } = validateGameResponse;

    if (!loading && (error || data)) {
      if (!error) {
        const { validateGame } = data;

        setGameName(validateGame.name);
        setGameType(validateGame.type || GAME_TYPE.GAME);
        if (gameLength !== 0) {
          setHasChanged(
            validateGame.name && gameId !== currentGame?.externalGameId
          );
          if (validateGame.name) {
            onSubmit({
              externalGameId: gameId,
              duration: Number(gameLength),
              type: validateGame.type || GAME_TYPE.GAME,
            });
          }
        }
      }

      if (hasValidated) {
        setIsValidated(true);
      }
    }
  }, [validateGameResponse, hasValidated]);

  const onChangeGameId = (e) => {
    setGameId(e.target.value);
    setIsValidated(false);
  };

  const onChangeGameLength = ({ value }) => {
    setGameLength(value);

    const lengthChanged = value !== currentGame?.duration && value !== 0;
    const valid = isValidated && gameName;
    const gameIdChanged = currentGame?.externalGameId !== gameId;

    if (lengthChanged && (valid || !gameIdChanged)) {
      setHasChanged(true);
      onSubmit({
        externalGameId: gameId,
        duration: Number(value),
        type: gameType,
      });
    }
  };

  return (
    <ContentWrapper>
      <InputButtonWrapper>
        <Input
          disabled={locked}
          onChange={onChangeGameId}
          onClick={(e) => e.stopPropagation()}
          onBlur={validateGameId}
          placeholder={t('backoffice.courses.examTab.gamePlaceHolder')}
          value={gameId}
        />
        <Button
          ariaLabel={t('backoffice.courses.examTab.validateGameId')}
          loading={validateGameResponse.loading}
          disabled={isValidated}
          onClick={validateGameId}
          sx={{ height: '40px' }}
          type="secondary"
        >
          {t('backoffice.courses.examTab.validateGameId')}
        </Button>
      </InputButtonWrapper>
      {isValidated && gameName && (
        <div style={{ marginTop: theme.spacing.s }}>
          <T color={theme.palette.lightPure}>Name: {gameName}</T>
          <T color={theme.palette.lightPure}>
            Type: {gameType === GAME_TYPE.ROLEPLAY ? 'Roleplay' : 'Game'}
          </T>
        </div>
      )}
      {isValidated && !gameName && (
        <T color={theme.palette.errorDark} sx={{ marginTop: theme.spacing.s }}>
          {t('backoffice.courses.examTab.checkError')}
        </T>
      )}
      <SelectWrapper>
        <Select
          disabled={locked}
          id="gameLengthSelect"
          name="gameLengthSelect"
          onChange={onChangeGameLength}
          items={[
            {
              label: t('backoffice.courses.lessonsTab.playingTime'),
              value: 0,
              key: 0,
            },
            ...GAME_LENGTH_OPTIONS.map((value) => ({
              label: t('backoffice.courses.lessonsTab.minutes', {
                amount: value / 60, // Display it in minutes
              }),
              value,
              key: value,
            })),
          ]}
          value={gameLength}
        />
      </SelectWrapper>
    </ContentWrapper>
  );
};

GameInput.propTypes = {
  currentGame: PropTypes.shape({
    externalGameId: PropTypes.string,
    duration: PropTypes.number,
    type: PropTypes.oneOf(Object.values(GAME_TYPE)),
  }),
  onSubmit: PropTypes.func.isRequired,
};

export default GameInput;
